import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { CheckCircle2, Copy, Download, LogIn, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";

const MAX_POLLS = 10;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  // The access_token is emitted by /checkout/stripe and threaded into the
  // Stripe success_url so the poll+download endpoints (SEC-004 mitigation)
  // can prove ownership of this session even without a user account.
  const accessToken = params.get("access_token");
  const [state, setState] = useState({ phase: "checking", data: null });
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: { message: "Falta session_id" } });
      return;
    }
    let cancelled = false;
    async function poll() {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`, {
          params: accessToken ? { access_token: accessToken } : undefined,
        });
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data });
          return;
        }
        if (attempts + 1 >= MAX_POLLS) {
          setState({ phase: "timeout", data });
          return;
        }
        setAttempts((n) => n + 1);
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setState({ phase: "error", data: { message: err?.response?.data?.detail || "Error de red" } });
      }
    }
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const copyPassword = (pw) => {
    navigator.clipboard.writeText(pw);
    toast.success("Contraseña copiada");
  };

  return (
    <div className="min-h-screen bg-[#F4F6F4] flex items-center justify-center px-4" data-testid="payment-success-page">
      <Card className="max-w-lg w-full p-8 border border-gray-200 shadow-none rounded-lg">
        {state.phase === "checking" && (
          <div className="text-center space-y-3" data-testid="payment-checking">
            <Loader2 className="w-10 h-10 mx-auto text-brand animate-spin" />
            <h1 className="font-display text-2xl text-brand-ink">Confirmando pago…</h1>
            <p className="text-sm text-gray-500">
              Estamos comprobando el estado con Stripe. No cierres esta página.
            </p>
            <p className="text-[10px] text-gray-400 font-mono-data">intento {attempts + 1}/{MAX_POLLS}</p>
          </div>
        )}

        {state.phase === "paid" && (
          <div className="space-y-5" data-testid="payment-paid">
            <div className="text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto text-brand" />
              <h1 className="font-display text-3xl text-brand-ink mt-3">¡Compra completada!</h1>
              <p className="text-sm text-gray-500 mt-1">
                Importe: <span className="font-mono-data">{state.data.amount_eur?.toFixed(2)} €</span>
                {state.data.invoice_number ? (
                  <>
                    {" "}· Factura{" "}
                    <span className="font-mono-data">{state.data.invoice_number}</span>
                  </>
                ) : null}
              </p>
            </div>

            {state.data.generated_password ? (
              <div className="border border-brand/30 bg-brand/5 rounded-lg p-4" data-testid="new-account-block">
                <p className="text-xs uppercase tracking-wider text-brand font-mono-data mb-2">
                  Cuenta creada
                </p>
                <p className="text-sm text-brand-ink">
                  Hemos creado una cuenta para <strong>{state.data.email}</strong>. Guarda esta
                  contraseña temporal — <strong>no volveremos a mostrarla</strong>:
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <code
                    className="flex-1 bg-white border border-gray-200 rounded-md px-3 py-2 font-mono-data text-brand-ink text-sm"
                    data-testid="new-account-password"
                  >
                    {state.data.generated_password}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyPassword(state.data.generated_password)}
                    data-testid="copy-password-btn"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid="existing-account-block">
                <p className="text-sm text-gray-700">
                  Los cursos ya se han añadido a tu cuenta <strong>{state.data.email}</strong>.
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              {state.data.invoice_number && (
                <a
                  href={`${API}/payments/invoice/${sessionId}${accessToken ? `?access_token=${encodeURIComponent(accessToken)}` : ""}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                  data-testid="download-invoice-btn"
                >
                  <Button variant="outline" className="w-full">
                    <Download className="w-4 h-4 mr-2" />
                    Descargar justificante de pago
                  </Button>
                </a>
              )}
              <Button
                onClick={() => navigate("/login")}
                className="flex-1 bg-brand hover:bg-brand-hover text-white"
                data-testid="go-to-login-btn"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Iniciar sesión
              </Button>
            </div>
            <p className="text-[11px] text-gray-500 text-center">
              También te enviaremos un email de confirmación con estos datos.
            </p>
          </div>
        )}

        {(state.phase === "timeout" || state.phase === "expired" || state.phase === "error") && (
          <div className="text-center space-y-3" data-testid="payment-error">
            <XCircle className="w-10 h-10 mx-auto text-amber-600" />
            <h1 className="font-display text-2xl text-brand-ink">
              {state.phase === "expired"
                ? "Sesión expirada"
                : state.phase === "timeout"
                ? "Estamos tardando más de lo normal"
                : "No pudimos confirmar el pago"}
            </h1>
            <p className="text-sm text-gray-500">
              Si el cobro se llegó a hacer, en unos minutos recibirás la factura por email. Si tienes dudas,
              contacta con nosotros.
            </p>
            <Link to="/">
              <Button variant="outline">Volver al catálogo</Button>
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}
